import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";
import { GetTickets, TGetTicket } from "api/rpc/2024-04/facilityAdmin/ticket/ticket";
import { ITicket } from "redux/reducers/models/ticket";
import { showError } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import Avatar from "components/avatar/Avatar";
import Search from "components/search/Search";
import DataTable from "../../../../components/dataTable/DataTable";
import DropFilter from "components/dropdown/DropFilter";

import "pages/secure/facility/product/Ticket.scss";
import { Badge } from "components/badge/Badge";

export default function Tickets() {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [tickets, setTickets] = useState<ITicket[]>(undefined);
  const [filters, setFilters] = useState<TGetTicket>({
    search: "",
    limit: 50,
    offset: 0,
    archived: 0,
  });

  const archivedFilterData = [
    {
      id: "archived",
      label: t("View Archived"),
    },
  ];

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadTickets(source.token);

    return () => source.cancel();
  }, [filters]);

  async function loadTickets(token?: CancelToken) {
    if (tickets !== undefined) {
      setTickets(undefined);
    }

    const ticketRes = await GetTickets(filters, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (ticketRes.status !== StatusCode.OK) {
      dispatch(
        showError(typeof ticketRes.data === "string" ? ticketRes.data : t("secure.facility.product.tickets.004")),
      );
    }

    setTickets(ticketRes.status === StatusCode.OK ? ticketRes.data : []);
  }

  const primaryAction = {
    content: t("secure.facility.product.tickets.001"),
    action: () => history.push("/admin/tickets/new"),
  };

  const secondaryActions = [
    {
      content: t("secure.facility.product.tickets.002"),
      action: () => history.push("/admin/tickets/stubs"),
    },
  ];

  return (
    <Page
      title={t("secure.facility.product.tickets.003")}
      narrow
      primaryAction={primaryAction}
      secondaryActions={secondaryActions}
    >
      <div className="mb-4">
        <DropFilter
          title={t("Archived")}
          filterType="Checkbox"
          filterData={archivedFilterData}
          filterIdPropFromData="id"
          filterLabelPropFromData="label"
          applyFilters={(filterReturn: any[]) => {
            if (filterReturn.length === 0) {
              // No filter applied - show all tickets
              setFilters(prev => ({ ...prev, archived: 0, offset: 0 }));
            } else {
              const selectedId = filterReturn[0].id;
              if (selectedId === "active") {
                // Show only active (non-archived) tickets
                setFilters(prev => ({ ...prev, archived: 0, offset: 0 }));
              } else if (selectedId === "archived") {
                // Show only archived tickets
                setFilters(prev => ({ ...prev, archived: 1, offset: 0 }));
              }
            }
          }}
          save={false}
          historyKey="ticket_archived_filter"
          leftAligned
        />
      </div>

      <div className="flex-grow mb-4">
        <Search
          historyKey={"ticket_table_search"}
          searchCallback={searchValue => {
            if ((searchValue.length === 0 || searchValue.length > 2) && searchValue !== filters.search) {
              setFilters(prev => ({ ...prev, search: searchValue, offset: 0 }));
            }
          }}
        />
      </div>

      <DataTable<ITicket>
        columns={[{ label: "Title" }, { label: "" }]}
        loading={tickets === undefined}
        footer={{
          tableLimit: filters.limit,
          tableOffset: filters.offset,
          disableNextOffset: !tickets || !(tickets?.length === filters.limit),
          handleTableOffset: direction =>
            setFilters(prev => ({
              ...prev,
              offset: direction === "prev" ? prev.offset - prev.limit : prev.offset + prev.limit,
            })),
        }}
      >
        {tickets?.map(ticket => (
          <tr key={ticket.id} className="clickable" onClick={() => history.push("/admin/tickets/" + String(ticket.id))}>
            <td>
              <div className="flex items-center gap-4">
                <Avatar source={ticket.logo_image} size="medium" />
                <span className="table-cell-text">
                  <p className="lead-text">{ticket.title}</p>
                  {ticket.subtitle ? <p className="support-text">{ticket.subtitle}</p> : null}
                </span>
              </div>
            </td>
            <td>
              {ticket?.archived && (
                <Badge type="error" size="small">
                  {t("secure.facility.product.tickets.006")}
                </Badge>
              )}
            </td>
          </tr>
        ))}
      </DataTable>
    </Page>
  );
}
